import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@evertrue/et-components';

/**
 * Live countdown timer for members in a wait step.
 * Shows time remaining until nextEventScheduledAt, ticking every second.
 * Displays "Wait complete" when the timer expires.
 */
const WaitCountdown = ({ targetDate, compact }) => {
  const [remaining, setRemaining] = useState(() => calcRemaining(targetDate));

  useEffect(() => {
    if (!targetDate) return;
    setRemaining(calcRemaining(targetDate));
    const interval = setInterval(() => {
      const r = calcRemaining(targetDate);
      setRemaining(r);
      if (r.total <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  if (!targetDate) return null;

  if (remaining.total <= 0) {
    return (
      <span className="wait-countdown is-complete" title="Wait step finished">
        <Icon icon="check" style={{ fontSize: 14 }} /> Wait complete
      </span>
    );
  }

  const parts = [];
  if (remaining.days > 0) parts.push(`${remaining.days}d`);
  if (remaining.hours > 0) parts.push(`${remaining.hours}h`);
  if (remaining.minutes > 0) parts.push(`${remaining.minutes}m`);
  // Only show seconds if less than 1 hour remaining
  if (remaining.days === 0 && remaining.hours === 0) {
    parts.push(`${remaining.seconds}s`);
  }

  const label = parts.join(' ');
  const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(targetDate);
  const utcDate = hasTimezone ? targetDate : targetDate + 'Z';
  const fullDate = new Date(utcDate).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  if (compact) {
    return (
      <span className="wait-countdown is-active" title={`Resumes ${fullDate}`}>
        <Icon icon="access-time" style={{ fontSize: 14 }} /> {label}
      </span>
    );
  }

  return (
    <span className="wait-countdown is-active" title={`Resumes ${fullDate}`}>
      <Icon icon="access-time" style={{ fontSize: 14 }} /> Waiting — {label} remaining
    </span>
  );
};

export function calcRemaining(targetDate) {
  if (!targetDate) return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  // Backend stores UTC datetimes but MySQL strips timezone info,
  // so the value may arrive without a Z suffix. Append Z if missing
  // to ensure JavaScript interprets it as UTC, not local time.
  const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(targetDate);
  const utcDate = hasTimezone ? targetDate : targetDate + 'Z';
  const diff = new Date(utcDate).getTime() - Date.now();
  if (diff <= 0) return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    total: diff,
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
}

WaitCountdown.propTypes = {
  targetDate: PropTypes.string,
  compact: PropTypes.bool,
};

export default WaitCountdown;
